import { useState, useEffect, useCallback, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { oppositeSide, type SidepanelSide } from '@shared/sidepanel/side'
import { useDragCursor } from '../../../hooks/use-drag-cursor'
import {
  centeredSidepanelVerticalOffset,
  clampSidepanelHeight,
  clampSidepanelVerticalOffset,
  defaultSidepanelHeight,
  type SidepanelVerticalLayout,
} from '../../../sidepanel-vertical-layout'
import {
  RESIZE_HANDLE_INSIDE_PX,
  RESIZE_HANDLE_OUTSIDE_PX,
  RESIZE_HANDLE_SIZE_PX,
  SURFACE_INSET_PX,
  SURFACE_RADIUS,
  SURFACE_RADIUS_INSIDE,
  RESIZE_PREVIEW_CLASS,
} from './resize-handle-metrics'

interface VerticalResizeHandleProps {
  edge: 'top' | 'bottom'
  layout: SidepanelVerticalLayout
  side: SidepanelSide
  onResizeComplete: (height: number, offset: number) => void
}

/**
 * Drag the panel's top or bottom edge to change its height.
 *
 * The mirror of `ResizeHandle`, rotated: the top edge grows the panel upward
 * (its bottom edge stays put) and the bottom edge grows it downward, so
 * whichever edge you did *not* grab never moves.
 */
export function VerticalResizeHandle({ edge, layout, side, onResizeComplete }: VerticalResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [showOverlay, setShowOverlay] = useState(false)
  const startYRef = useRef(0)
  const startHeightRef = useRef(0)
  const startTopRef = useRef(0)
  const previewRef = useRef({ height: 0, top: 0 })
  const previewLineRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)
  const listenersRef = useRef<{
    move: (e: PointerEvent) => void
    up: () => void
  } | null>(null)

  // The handle sits on the panel's inner edge (away from the screen edge), so
  // its CSS anchor flips with the panel side.
  const innerEdge = oppositeSide(side)

  const positionPreview = useCallback(() => {
    if (!previewLineRef.current) {
      return
    }
    const { height, top } = previewRef.current
    // Relative to the root, which is still at its pre-drag geometry.
    previewLineRef.current.style.top = `${top - startTopRef.current + SURFACE_INSET_PX}px`
    previewLineRef.current.style.height = `${Math.max(0, height - 2 * SURFACE_INSET_PX)}px`
  }, [])

  const cleanupListeners = useCallback(() => {
    if (listenersRef.current) {
      document.removeEventListener('pointermove', listenersRef.current.move)
      document.removeEventListener('pointerup', listenersRef.current.up)
      listenersRef.current = null
    }
  }, [])

  // Cleanup listeners on unmount (e.g. sidepanel closes mid-drag)
  useEffect(() => {
    return () => {
      cleanupListeners()
    }
  }, [cleanupListeners])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation()
      e.preventDefault()
      handleRef.current?.setPointerCapture(e.pointerId)
      const { bandTop, bandHeight, height, top } = layout
      startYRef.current = e.clientY
      startHeightRef.current = height
      startTopRef.current = top
      previewRef.current = { height, top }
      setIsDragging(true)

      cleanupListeners()

      const onPointerMove = (ev: PointerEvent) => {
        const delta = ev.clientY - startYRef.current
        const heightDelta = edge === 'bottom' ? delta : -delta
        const center = startTopRef.current + startHeightRef.current / 2
        const maxHeight = ev.altKey
          ? 2 * Math.min(center - bandTop, bandTop + bandHeight - center)
          : edge === 'bottom'
            ? bandTop + bandHeight - startTopRef.current
            : startTopRef.current + startHeightRef.current - bandTop
        const nextHeight = clampSidepanelHeight(startHeightRef.current + heightDelta * (ev.altKey ? 2 : 1), maxHeight)
        const appliedHeightDelta = nextHeight - startHeightRef.current
        // Option resizes symmetrically around the panel's center. Without it,
        // the edge opposite the grabbed handle remains fixed.
        const nextTop = ev.altKey
          ? startTopRef.current - appliedHeightDelta / 2
          : edge === 'bottom'
            ? startTopRef.current
            : startTopRef.current + startHeightRef.current - nextHeight
        const anchoredOffset = nextTop - bandTop
        const nextOffset = clampSidepanelVerticalOffset(anchoredOffset, bandHeight, nextHeight)

        previewRef.current = { height: nextHeight, top: bandTop + nextOffset }
        positionPreview()
      }

      const onPointerUp = () => {
        cleanupListeners()
        const { height: finalHeight, top: finalTop } = previewRef.current
        setShowOverlay(false)
        setIsDragging(false)
        onResizeComplete(finalHeight, finalTop - bandTop)
      }

      listenersRef.current = { move: onPointerMove, up: onPointerUp }
      document.addEventListener('pointermove', onPointerMove)
      document.addEventListener('pointerup', onPointerUp)
    },
    [layout, edge, onResizeComplete, cleanupListeners, positionPreview],
  )

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      cleanupListeners()
      setShowOverlay(false)
      setIsDragging(false)
      const height = defaultSidepanelHeight(layout.bandHeight)
      onResizeComplete(height, centeredSidepanelVerticalOffset(layout.bandHeight, height))
    },
    [onResizeComplete, layout.bandHeight, cleanupListeners],
  )

  useEffect(() => {
    if (!isDragging) {
      setShowOverlay(false)
      return
    }
    const timer = setTimeout(() => setShowOverlay(true), 200)
    return () => clearTimeout(timer)
  }, [isDragging])

  // Sync overlay position on first render (catches drag delta accumulated during 200ms delay)
  useEffect(() => {
    if (!showOverlay) return
    positionPreview()
  }, [showOverlay, positionPreview])

  useDragCursor(isDragging, 'ns-resize')

  // Apply brightness filter to sidepanel content during drag
  useEffect(() => {
    if (!showOverlay) return
    const content = document.querySelector<HTMLElement>('[data-sidepanel-content]')
    if (content) {
      content.style.filter = 'brightness(0.75)'
      content.style.transition = 'filter 0.15s ease'
    }
    return () => {
      if (content) {
        content.style.filter = ''
        content.style.transition = ''
      }
    }
  }, [showOverlay])

  return (
    <>
      {/* Hit area — 4px into the gutter, 2px into the panel. Stops short of the
          free-corner handle so diagonal resize owns that region. */}
      <div
        ref={handleRef}
        onPointerDown={handlePointerDown}
        onDoubleClick={handleDoubleClick}
        /* z-40, matching the width handle: above the view router (z-6) so the
           panel stays resizable while a View is open. */
        className="group pointer-events-auto absolute z-40 cursor-ns-resize"
        style={{
          [edge]: SURFACE_INSET_PX - RESIZE_HANDLE_OUTSIDE_PX,
          height: RESIZE_HANDLE_SIZE_PX,
          [side]: SURFACE_INSET_PX,
          [innerEdge]: `calc(var(--sidepanel-left-padding, 12px) + ${RESIZE_HANDLE_INSIDE_PX}px)`,
        }}
      >
        {/* Hover line indicator — pinned to the surface edge, spanning the
            straight part of the card between the docked side and the corner. */}
        <div
          className={twMerge(
            'border-on-light absolute h-0.5 rounded-full border drop-shadow-md transition-opacity duration-150 ease-out',
            isDragging ? 'opacity-0' : 'bg-white-8 opacity-0 group-hover:opacity-100 group-hover:delay-150',
          )}
          style={{
            [edge]: RESIZE_HANDLE_OUTSIDE_PX,
            // Anchor edge is the surface; flip the shift so the 2px line centers on it.
            transform: edge === 'top' ? 'translateY(-50%)' : 'translateY(50%)',
            [side]: SURFACE_RADIUS,
            [innerEdge]: SURFACE_RADIUS_INSIDE,
          }}
        />
      </div>

      {showOverlay && (
        /* Traces the surface card exactly — same 12px gutter, same 16px radius —
           so the preview reads as the panel's new silhouette. */
        <div
          ref={previewLineRef}
          className={RESIZE_PREVIEW_CLASS}
          style={{
            [innerEdge]: 'var(--sidepanel-left-padding, 12px)',
            [side]: SURFACE_INSET_PX,
          }}
        />
      )}
    </>
  )
}
